<template>
  <div class="space-y-6">
    <!-- 页面标题 -->
    <div>
      <h2 class="text-2xl font-semibold text-gray-700">系统日志</h2>
      <div class="flex items-center mt-2">
        <router-link to="/admin/dashboard" class="text-gray-600 hover:text-gray-800">首页</router-link>
        <span class="mx-2 text-gray-400">•</span>
        <span class="text-gray-600">系统管理</span>
        <span class="mx-2 text-gray-400">•</span>
        <span class="text-gray-600">系统日志</span>
      </div>
    </div>
    
    <!-- 存储信息卡片 -->
    <div class="bg-white shadow rounded-lg overflow-hidden transition-all hover:shadow-md">
      <div class="px-6 py-4 bg-gradient-to-r from-green-500 to-green-600">
        <div class="flex items-center justify-between">
          <h3 class="text-lg font-medium text-white">存储信息</h3>
          <i class="fas fa-cloud text-white text-xl transition-transform hover:scale-110"></i>
        </div>
      </div>
      <div class="px-6 py-4">
        <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center">
          <div class="mb-4 sm:mb-0">
            <div class="text-sm text-gray-500 mb-1">存储路径</div>
            <div class="flex items-center">
              <span class="font-semibold text-gray-700 mr-1">{{ storagePath }}</span>
              <span v-if="storageConnected" class="text-green-500 transition-transform hover:scale-110" title="存储连接正常">
                <i class="fas fa-check-circle"></i>
              </span>
              <span v-else class="text-red-500 transition-transform hover:scale-110" title="存储连接异常">
                <i class="fas fa-exclamation-circle"></i>
              </span>
            </div>
          </div>
          <div>
            <div class="text-sm text-gray-500 mb-1">区域</div>
            <div class="font-semibold text-gray-700 flex items-center">
              <i class="fas fa-globe-asia mr-2 text-blue-500"></i>
              {{ storageRegion }}
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 主内容区域 -->
    <div class="grid grid-cols-1 md:grid-cols-5 gap-6">
      <!-- 左侧目录栏 -->
      <div class="bg-white shadow rounded-lg overflow-hidden md:col-span-1 hover:shadow-md transition-all">
        <div class="px-6 py-4 bg-gradient-to-r from-blue-500 to-blue-600">
          <div class="flex items-center justify-between">
            <h3 class="text-lg font-medium text-white">日志目录</h3>
            <i class="fas fa-folder text-white text-xl transition-transform hover:scale-110"></i>
          </div>
        </div>
        <div class="max-h-[70vh] overflow-auto custom-scrollbar">
          <a 
            href="#" 
            v-for="category in logCategories" 
            :key="category.id"
            @click.prevent="loadLogs(category.id)"
            :class="[
              'block py-3 px-4 border-b border-gray-100 hover:bg-gray-50 transition-colors duration-200',
              currentCategory === category.id ? 'bg-blue-50 text-blue-600 font-medium border-l-4 border-blue-500' : 'text-gray-600 border-l-4 border-transparent'
            ]"
          >
            <i :class="[
              'mr-2 transition-transform duration-200', 
              currentCategory === category.id ? 'fas fa-folder-open text-blue-500 transform scale-110' : 'fas fa-folder text-gray-400'
            ]"></i>
            {{ category.name }}
          </a>
        </div>
      </div>
      
      <!-- 右侧内容区 -->
      <div class="bg-white shadow rounded-lg overflow-hidden md:col-span-4">
        <!-- 搜索和功能区 -->
        <div class="px-6 py-4 bg-gradient-to-r from-purple-500 to-purple-600">
          <div class="flex items-center justify-between">
            <h3 class="text-lg font-medium text-white">{{ getCurrentCategoryName() }}</h3>
            <div>
              <span class="text-white text-sm mr-2" v-if="logs.length > 0">共{{ totalLogs }}个文件</span>
              <button @click="refreshLogs" class="text-white hover:text-gray-100 p-1" title="刷新">
                <i class="fas fa-sync-alt"></i>
              </button>
            </div>
          </div>
        </div>
        
        <!-- 错误提示 -->
        <div v-if="errorMessage" class="p-4 mb-4 text-sm text-red-700 bg-red-100 rounded-lg mx-6 mt-4" role="alert">
          <div class="font-medium">错误!</div>
          <div>{{ errorMessage }}</div>
        </div>
        
        <!-- 搜索表单 -->
        <div class="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <form @submit.prevent="searchLogs" class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label for="keyword" class="block text-sm font-medium text-gray-700 mb-1">文件名搜索</label>
              <FormInput
                id="block"
                placeholder="搜索文件名称"
                prefix-icon="fas fa-search"
                v-model="filters.keyword"
                :clearable="true"
              />
            </div>
            
            <div>
              <label for="scope" class="block text-sm font-medium text-gray-700 mb-1">日志类型</label>
              <FormSelect
                id="scope"
                v-model="filters.scope"
                :options="scopeOptions"
                placeholder="选择日志类型"
              />
            </div>
            
            <div class="flex items-center gap-2">
              <button type="submit" class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 shadow hover:shadow-md">
                <i class="fas fa-search mr-1"></i> 搜索
              </button>
              
              <button type="button" @click="refreshLogs" class="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200 shadow hover:shadow-md">
                <i class="fas fa-sync-alt mr-1 hover:animate-spin"></i> 刷新
              </button>
            </div>
          </form>
        </div>
        
        <!-- 文件表格 -->
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  名称
                </th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  大小
                </th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  类型
                </th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  最后修改时间
                </th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-if="loading">
                <td colspan="5" class="px-6 py-10 text-center text-gray-500">
                  <div class="flex flex-col items-center">
                    <div class="loader mb-3"></div>
                    <p>加载中...</p>
                  </div>
                </td>
              </tr>
              <tr v-else-if="logs.length === 0">
                <td colspan="5" class="px-6 py-10 text-center text-gray-500">
                  <div class="flex flex-col items-center">
                    <i class="fas fa-folder-open text-gray-400 text-3xl mb-3 animate-pulse"></i>
                    <p>没有找到日志文件</p>
                    <p v-if="currentCategory" class="mt-1 text-sm">
                      尝试查看 <a href="#" @click.prevent="loadLogs('')" class="text-blue-600 hover:text-blue-800 hover:underline transition-colors">全部日志</a> 或检查存储配置
                    </p>
                  </div>
                </td>
              </tr>
              <tr v-for="log in logs" :key="log.path" class="hover:bg-gray-50 transition-colors duration-150">
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center">
                    <template v-if="log.isDirectory">
                      <i class="fas fa-folder text-yellow-400 mr-3 transition-transform hover:scale-110"></i>
                      <a 
                        href="#" 
                        @click.prevent="loadLogs(log.scope, log.path)"
                        class="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                      >
                        {{ log.name }}/
                      </a>
                    </template>
                    <template v-else>
                      <i class="fas fa-file-alt text-gray-400 mr-3 transition-transform hover:scale-110"></i>
                      <span class="text-sm font-medium text-gray-900">{{ log.name }}</span>
                    </template>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ log.size }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <template v-if="log.isDirectory">
                    <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800 border border-yellow-200">
                      目录
                    </span>
                  </template>
                  <template v-else>
                    <span 
                      class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full shadow-sm"
                      :class="getLogTypeClass(log.scope)"
                    >
                      {{ getLogTypeName(log.scope) }}
                    </span>
                  </template>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ log.lastModified }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <template v-if="!log.isDirectory">
                    <a href="#" @click.prevent="viewLogContent(log)" class="text-blue-600 hover:text-blue-900 mr-3 hover:underline transition-colors">
                      <i class="fas fa-eye mr-1"></i>查看
                    </a>
                    <a href="#" @click.prevent="downloadLog(log)" class="text-blue-600 hover:text-blue-900 mr-3 hover:underline transition-colors">
                      <i class="fas fa-download mr-1"></i>下载
                    </a>
                    <a href="#" @click.prevent="confirmDeleteLog(log)" class="text-red-600 hover:text-red-900 hover:underline transition-colors">
                      <i class="fas fa-trash-alt mr-1"></i>删除
                    </a>
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <!-- 分页 -->
        <div class="px-6 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
          <div class="text-sm text-gray-700" v-if="logs.length > 0">
            显示 <span class="font-medium">{{ (currentPage - 1) * pageSize + 1 }}</span> - <span class="font-medium">{{ Math.min(currentPage * pageSize, totalLogs) }}</span> 条，共 <span class="font-medium">{{ totalLogs }}</span> 条记录
          </div>
          <div class="flex items-center space-x-2">
            <span class="text-sm text-gray-500">每页显示:</span>
            <FormSelect
              v-model="pageSize"
              :options="pageSizeOptions"
              class="w-32"
              @change="changePageSize"
            />
            <div v-if="totalPages > 1" class="flex shadow-sm">
              <button 
                @click="prevPage" 
                :disabled="currentPage <= 1"
                :class="[
                  'relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium transition-colors duration-200',
                  currentPage <= 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50'
                ]"
              >
                <span class="sr-only">上一页</span>
                <i class="fas fa-chevron-left h-5 w-5"></i>
              </button>
              <span class="relative inline-flex items-center px-4 py-2 border-t border-b border-gray-300 bg-white text-sm font-medium text-gray-700">
                第 <span class="mx-1 font-bold text-blue-600">{{ currentPage }}</span> 页，共 <span class="mx-1 font-bold">{{ totalPages }}</span> 页
              </span>
              <button 
                @click="nextPage" 
                :disabled="currentPage >= totalPages"
                :class="[
                  'relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium transition-colors duration-200',
                  currentPage >= totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50'
                ]"
              >
                <span class="sr-only">下一页</span>
                <i class="fas fa-chevron-right h-5 w-5"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 日志内容模态框 -->
    <Modal
      :visible="logModalVisible"
      title="日志详情"
      @close="closeModal"
      max-width="4xl"
      full-height
    >
      <div v-if="logContentLoading" class="text-center py-10">
        <div class="loader mx-auto"></div>
        <p class="mt-2 text-gray-500">正在加载日志内容...</p>
      </div>
      <div v-else>
        <pre class="bg-gray-50 p-4 rounded-md text-sm text-gray-800 overflow-auto max-h-[60vh] border border-gray-200 shadow-inner">{{ logContent }}</pre>
      </div>
      
      <template #footer>
        <div class="flex justify-end space-x-3">
          <button 
            v-if="selectedLog"
            @click="downloadLog(selectedLog)" 
            class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 shadow hover:shadow-md"
          >
            <i class="fas fa-download mr-2"></i>下载完整日志
          </button>
          <button 
            v-if="selectedLog"
            @click="confirmDeleteLog(selectedLog, true)" 
            class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200 shadow hover:shadow-md"
          >
            <i class="fas fa-trash-alt mr-2"></i>删除日志
          </button>
          <button 
            @click="closeModal" 
            class="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
          >
            关闭
          </button>
        </div>
      </template>
    </Modal>
    
    <!-- 删除确认模态框 -->
    <Modal
      :visible="deleteModalVisible"
      title="确认删除"
      @close="cancelDelete"
      max-width="md"
    >
      <div class="p-4">
        <div class="flex items-center text-red-600 mb-4">
          <i class="fas fa-exclamation-triangle text-2xl mr-3"></i>
          <p class="text-gray-700 font-medium">确定要删除以下日志文件吗？</p>
        </div>
        <p class="font-medium text-gray-900 mt-2 p-3 bg-gray-50 rounded border border-gray-200" v-if="logToDelete">{{ logToDelete.name }}</p>
        <p class="text-red-600 text-sm mt-4 italic">警告：此操作不可撤销！</p>
      </div>
      
      <template #footer>
        <div class="flex justify-end space-x-3">
          <button 
            @click="cancelDelete" 
            class="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
          >
            取消
          </button>
          <button 
            @click="deleteLog" 
            class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200 shadow hover:shadow-md"
          >
            <i class="fas fa-trash-alt mr-2"></i>删除
          </button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import Modal from '@/components/common/Modal.vue';
import AdminApiClient from '@/utils/admin-api-client';
import FormSelect from '@/components/common/FormSelect.vue';
import FormInput from '@/components/common/FormInput.vue';

const apiClient = new AdminApiClient();

// 状态变量
const loading = ref(false);
const logs = ref([]);
const errorMessage = ref('');
const currentCategory = ref('');
const currentPath = ref('');
const totalLogs = ref(0);
const currentPage = ref(1);
const pageSize = ref(20);
const storageConnected = ref(true);
const storagePath = ref('pw/development');
const storageRegion = ref('us-east-1');

// 日志类型选项
const scopeOptions = [
  { value: '', label: '全部类型' },
  { value: 'callback', label: '回调日志' },
  { value: 'access', label: '访问日志' },
  { value: 'laravel', label: '应用日志' },
  { value: 'error', label: '错误日志' },
  { value: 'game', label: '游戏日志' },
  { value: 'sql', label: 'SQL日志' }
];

// 分页选项
const pageSizeOptions = [
  { value: 10, label: '10 条/页' },
  { value: 20, label: '20 条/页' },
  { value: 50, label: '50 条/页' }
];

// 日志分类
const logCategories = [
  { id: '', name: '全部日志' },
  { id: 'callback', name: '回调日志 (callback)' },
  { id: 'access', name: '访问日志 (access)' },
  { id: 'laravel', name: '应用日志 (laravel)' },
  { id: 'error', name: '错误日志 (error)' },
  { id: 'game', name: '游戏日志 (game)' },
  { id: 'sql', name: 'SQL日志 (sql)' }
];

// 筛选条件
const filters = reactive({
  keyword: '',
  scope: ''
});

// 日志内容模态框
const logModalVisible = ref(false);
const logContentLoading = ref(false);
const logContent = ref('');
const selectedLog = ref(null);

// 删除确认模态框
const deleteModalVisible = ref(false);
const logToDelete = ref(null);

// 计算总页数
const totalPages = computed(() => {
  return Math.ceil(totalLogs.value / pageSize.value);
});

// 获取当前分类名称
function getCurrentCategoryName() {
  if (!currentCategory.value) {
    return '全部日志';
  }
  
  const category = logCategories.find(c => c.id === currentCategory.value);
  return category ? category.name : currentCategory.value;
}

// 加载日志列表
async function loadLogs(category, path = '') {
  loading.value = true;
  errorMessage.value = '';
  
  // 更新当前状态
  currentCategory.value = category;
  currentPath.value = path;
  currentPage.value = 1;
  
  // 清空筛选条件
  filters.keyword = '';
  filters.scope = category;
  
  // 清空当前列表，确保在加载时不显示旧数据
  logs.value = [];
  
  try {
    const params = {
      scope: category,
      path: path,
      page: currentPage.value,
      per_page: pageSize.value
    };
    
    const response = await apiClient.getSystemLogs(params);
    
    if (!response.success) {
      errorMessage.value = response.message;
      logs.value = [];
      totalLogs.value = 0;
    } else {
      logs.value = response.data.logs || [];
      totalLogs.value = response.totalCount || 0;
      
      // 更新存储路径信息
      if (response.currentPath) {
        storagePath.value = response.currentPath;
      }
      
      // 检查存储连接状态
      storageConnected.value = true;
    }
  } catch (error) {
    console.error('加载日志列表失败:', error);
    
    // 根据错误状态码处理不同的错误情况
    if (error.status === 401) {
      // 401错误已由API客户端处理，这里不需要额外处理
      errorMessage.value = '未登录或登录已过期，正在跳转到登录页面...';
    } else if (error.status === 403) {
      errorMessage.value = '您没有权限访问日志系统';
    } else {
      errorMessage.value = '加载日志列表失败: ' + error.message;
    }
    
    logs.value = [];
    totalLogs.value = 0;
    storageConnected.value = false;
  } finally {
    loading.value = false;
  }
}

// 搜索日志
async function searchLogs() {
  loading.value = true;
  errorMessage.value = '';
  
  // 重置分页
  currentPage.value = 1;
  
  try {
    const params = {
      keyword: filters.keyword,
      scope: filters.scope,
      page: currentPage.value,
      per_page: pageSize.value
    };
    
    const response = await apiClient.searchLogs(params);
    
    if (response.error) {
      errorMessage.value = response.error;
      logs.value = [];
      totalLogs.value = 0;
    } else {
      logs.value = response.logs || [];
      totalLogs.value = response.totalCount || 0;
    }
  } catch (error) {
    console.error('搜索日志失败:', error);
    
    // 根据错误状态码处理不同的错误情况
    if (error.status === 401) {
      // 401错误已由API客户端处理，这里不需要额外处理
      errorMessage.value = '未登录或登录已过期，正在跳转到登录页面...';
    } else if (error.status === 403) {
      errorMessage.value = '您没有权限搜索日志';
    } else {
      errorMessage.value = '搜索日志失败: ' + error.message;
    }
    
    logs.value = [];
    totalLogs.value = 0;
  } finally {
    loading.value = false;
  }
}

// 刷新日志列表
function refreshLogs() {
  if (filters.keyword) {
    searchLogs();
  } else {
    loadLogs(currentCategory.value, currentPath.value);
  }
}

// 查看日志内容
async function viewLogContent(log) {
  logModalVisible.value = true;
  logContentLoading.value = true;
  logContent.value = '';
  selectedLog.value = log;
  
  try {
    const response = await apiClient.viewLogContent({
      scope: log.scope,
      file: log.path
    });
    
    if (response.error) {
      logContent.value = '加载日志内容失败: ' + response.error;
    } else {
      logContent.value = response.content || '(空日志内容)';
    }
  } catch (error) {
    console.error('查看日志内容失败:', error);
    
    // 根据错误状态码处理不同的错误情况
    if (error.status === 401) {
      // 401错误已由API客户端处理，这里不需要额外处理
      logContent.value = '未登录或登录已过期，正在跳转到登录页面...';
    } else if (error.status === 403) {
      logContent.value = '您没有权限查看此日志内容';
    } else {
      logContent.value = '加载日志内容失败: ' + error.message;
    }
  } finally {
    logContentLoading.value = false;
  }
}

// 关闭模态框
function closeModal() {
  logModalVisible.value = false;
  selectedLog.value = null;
}

// 下载日志
function downloadLog(log) {
  window.location.href = apiClient.getLogDownloadUrl({
    scope: log.scope,
    file: log.path
  });
}

// 确认删除日志
function confirmDeleteLog(log, closeLogModal = false) {
  logToDelete.value = log;
  deleteModalVisible.value = true;
  
  if (closeLogModal) {
    logModalVisible.value = false;
  }
}

// 取消删除
function cancelDelete() {
  deleteModalVisible.value = false;
  logToDelete.value = null;
}

// 删除日志
async function deleteLog() {
  if (!logToDelete.value) return;
  
  try {
    const response = await apiClient.deleteLog({
      scope: logToDelete.value.scope,
      file: logToDelete.value.path
    });
    
    if (response.error) {
      alert('删除日志失败: ' + response.error);
    } else {
      // 关闭删除确认模态框
      deleteModalVisible.value = false;
      logToDelete.value = null;
      
      // 刷新日志列表
      refreshLogs();
    }
  } catch (error) {
    console.error('删除日志失败:', error);
    
    // 根据错误状态码处理不同的错误情况
    if (error.status === 401) {
      // 401错误已由API客户端处理，这里不需要额外处理
      alert('未登录或登录已过期，正在跳转到登录页面...');
    } else if (error.status === 403) {
      alert('您没有权限删除此日志');
    } else {
      alert('删除日志失败: ' + error.message);
    }
  }
}

// 分页控制
function prevPage() {
  if (currentPage.value > 1) {
    currentPage.value--;
    refreshLogs();
  }
}

function nextPage() {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
    refreshLogs();
  }
}

// 改变每页显示数量
function changePageSize() {
  currentPage.value = 1;
  refreshLogs();
}

// 获取日志类型样式类
function getLogTypeClass(scope) {
  const typeClasses = {
    'error': 'bg-red-100 text-red-800',
    'callback': 'bg-yellow-100 text-yellow-800',
    'access': 'bg-green-100 text-green-800',
    'laravel': 'bg-blue-100 text-blue-800',
    'game': 'bg-indigo-100 text-indigo-800',
    'sql': 'bg-purple-100 text-purple-800'
  };
  
  return typeClasses[scope] || 'bg-gray-100 text-gray-800';
}

// 获取日志类型名称
function getLogTypeName(scope) {
  const typeNames = {
    'error': '错误日志',
    'callback': '回调日志',
    'access': '访问日志',
    'laravel': '应用日志',
    'game': '游戏日志',
    'sql': 'SQL日志'
  };
  
  return typeNames[scope] || scope;
}

// 页面初始化
onMounted(() => {
  // 加载初始日志列表
  loadLogs('');
});
</script>

<style scoped>
.loader {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 添加一些卡片过渡效果 */
.bg-white {
  transition: all 0.3s ease;
}

.bg-white:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

/* 表格样式优化 */
table {
  width: 100%;
  table-layout: fixed;
}

thead th {
  position: sticky;
  top: 0;
  background-color: #f9fafb;
  z-index: 10;
}

/* 文件列表美化 */
tr.hover\:bg-gray-50:hover {
  background-color: #f9fafb !important;
  cursor: pointer;
}

/* 优化按钮过渡效果 */
button {
  transition: all 0.2s ease;
}

button:hover {
  transform: translateY(-1px);
}

button:active {
  transform: translateY(1px);
}

/* 优化模态框内容 */
pre {
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  line-height: 1.4;
}

/* 改进滚动条样式 */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: #cdcdcd;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 日志类型徽章优化 */
.rounded-full {
  transition: all 0.2s ease;
}

.rounded-full:hover {
  transform: scale(1.05);
}

/* 存储状态指示器样式 */
.fas.fa-check-circle,
.fas.fa-exclamation-circle {
  transition: all 0.2s ease;
}

.fas.fa-check-circle:hover,
.fas.fa-exclamation-circle:hover {
  transform: scale(1.2);
}
</style> 